
/*
 *   File : select-box.js
 *   Author : https://evoqins.com
 *   Description : select box component
 *   Integrations : ' Select , components from react-select'
 *   Version : 1.0.0
*/

// custom packages
import React, { memo } from 'react';
import Select, { components } from "react-select";

// custom components
import { Icon } from '../Icon';
import { getAppliedZoom } from '../../Helper/display-scale';

// custom styles
import color from '../../Style/color.scss';
import style from "../../Style/Component/input.module.scss";

/** react-select menuPortal uses getBoundingClientRect (visual px). Under
 *  html { zoom }, fixed left/top are scaled again — divide by applied zoom. */
const _scalePortalCoord = (value, zoom) => {
    if (zoom === 1 || value == null) return value;
    if (typeof value === "number") return value / zoom;
    if (typeof value === "string" && value.endsWith("px")) {
        const n = parseFloat(value);
        return Number.isFinite(n) ? `${n / zoom}px` : value;
    }
    return value;
};

const _menuPortalStyle = (base) => {
    const zoom = getAppliedZoom() || 1;
    return {
        ...base,
        zIndex: 9999,
        top: _scalePortalCoord(base.top, zoom),
        left: _scalePortalCoord(base.left, zoom),
        width: _scalePortalCoord(base.width, zoom),
        height: _scalePortalCoord(base.height, zoom),
    };
};

const CustomSelectBox = memo((props) => {

    // handle select change
    // const _handleSelectChange = (selected) => {
    //     props.onChange(selected);
    // };

    // handle select change
    // const _handleSelectChange = (selected) => {
    //     if (!props.isMulti) {
    //         props.onChange(selected);
    //         return;
    //     }

    //     if (!selected || selected.length === 0) {
    //         props.onChange([]);
    //         return;
    //     }

    //     console.log("selected", selected);


    //     // Remove "All" if any other option is selected
    //     const hasOtherSelected = selected.some(opt => opt.value !== null);

    //     const updated = hasOtherSelected
    //         ? selected.filter(opt => opt.value !== null) // remove All
    //         : selected; // keep All if nothing else selected

    //     console.log("updated", updated);


    //     props.onChange(updated);
    // };


    // const _handleSelectChange = (selected, action) => {
    //     if (!props.isMulti) {
    //         props.onChange(selected);
    //         return;
    //     }

    //     if (!selected || selected.length === 0) {
    //         props.onChange([]);
    //         return;
    //     }

    //     const allOption = props.options.find(opt => opt.value === null);

    //     // User clicked "All"
    //     if (selected.some(opt => opt.value === null)) {
    //         if (action.action === "select-option" && action.option.value === null) {
    //             // Select All → clear others
    //             props.onChange([allOption]);
    //             return;
    //         } else if (action.action === "deselect-option" && action.option.value === null) {
    //             // Deselect All → allow selecting others
    //             props.onChange([]);
    //             return;
    //         }
    //     }

    //     // Remove "All" if present
    //     const updated = selected.filter(opt => opt.value !== null);

    //     // Keep reference to original option objects
    //     const updatedWithRef = props.options.filter(opt =>
    //         updated.some(u => u.value === opt.value)
    //     );

    //     props.onChange(updatedWithRef);
    // };

    const _handleSelectChange = (selected, action) => {
        if (!props.isMulti) {
            props.onChange(selected);
            return;
        }

        // If nothing is selected or cleared, default to "All"
        if (!selected || selected.length === 0) {
            const allOption = props.options.find(opt => opt.value === null);
            if (allOption) {
                props.onChange([allOption]);
            } else {
                props.onChange([]);
            }
            return;
        }

        const allOption = props.options.find(opt => opt.value === null);

        // User clicked "All"
        if (selected.some(opt => opt.value === null)) {
            if (action.action === "select-option" && action.option.value === null) {
                // Select All → clear others
                props.onChange([allOption]);
                return;
            } else if (action.action === "deselect-option" && action.option.value === null) {
                // Deselect All → default back to All
                props.onChange([allOption]);
                return;
            }
        }

        // Remove "All" if present
        const updated = selected.filter(opt => opt.value !== null);

        // If no other options remain, default to All
        if (updated.length === 0) {
            props.onChange([allOption]);
            return;
        }

        // Keep reference to original option objects
        const updatedWithRef = props.options.filter(opt =>
            updated.some(u => u.value === opt.value)
        );

        props.onChange(updatedWithRef);
    };


    const CheckboxOption = (optionProps) => {
        return (
            <components.Option {...optionProps}>
                <div className="d-flex align-items-center gap-2 ">
                    <Icon
                        icon={optionProps.isSelected ? "checkbox-active" : "checkbox-inactive"}
                        size={16}
                        className="cursor-pointer"
                    />

                    <span>{optionProps.label}</span>
                </div>
            </components.Option>
        );
    };


    // style one 
    const CUSTOM_STYLES = {
        container: (defaultStyles, state) => ({
            ...defaultStyles,
            width: "100%",
            '&: focus- visible': {
                outline: 'none'
            }
        }),
        placeholder: (defaultStyles) => ({
            ...defaultStyles,
            margin: "0",
            fontFamily: "Myriad Pro",
            fontSize: "0.875rem",
            lineHeight: "1rem",
            color: color.quick_silver
        }),
        menuList: (defaultStyles) => ({
            ...defaultStyles,
            padding: '0',
            maxHeight: '11.25rem',
            '&::-webkit-scrollbar-thumb': {
                background: color.lavender_blue,
                borderRadius: "0.25rem",
            },
            '&::-webkit-scrollbar': {
                width: "0.25rem",
                display: "block",
            }
        }),
        control: (defaultStyles, state) => ({
            ...defaultStyles,
            width: props.controlWidth ? props.controlWidth : "15rem",
            minHeight: props.height ? props.height : "2.5rem",
            borderRadius: "0.5rem",
            border: props.error ? `0.063rem solid ${color.carmine_pink}` : props.borderColor ? props.borderColor : `0.063rem solid ${color.light_purple}`,
            backgroundColor: color.white,
            boxShadow: 'none',
            overflow: "hidden",
            padding: props.padding ? props.padding : "0rem 0.75rem",
            "&:hover": {
                cursor: 'pointer',
            }
        }),
        singleValue: (defaultStyles, state) => ({
            ...defaultStyles,
            textAlign: "left",
            color: color.dark_gray,
            fontFamily: "Myriad Pro",
            fontSize: "0.875rem",
            lineHeight: "1rem",
        }),
        valueContainer: (defaultStyles) => ({
            ...defaultStyles,
            padding: "0rem",
            ...(props.type === 3 && {
                display: "flex",
                flexWrap: "nowrap",
                overflowX: "auto",
                maxWidth: "100%",
                scrollbarWidth: "none", // Firefox
                "&::-webkit-scrollbar": {
                    display: "none", // Chrome, Safari, Edge
                },
            })
        }),
        menu: (defaultStyles) => ({
            ...defaultStyles,
            left: "0",
            boxShadow: ` 8px 16px 56px 0px #0000001F`,
            borderRadius: "0.5rem",
            zIndex: 999,
            width: props.width ? "15rem" : "100%",
            padding: '0.5rem 0rem',
            marginBottom: '0.25rem',
            marginTop: '0.25rem'
        }),
        menuPortal: _menuPortalStyle,
        dropdownIndicator: (defaultStyles, state) => ({
            ...defaultStyles,
            padding: "0",
            transition: "transform 0.1s",
            transform: state.selectProps.menuIsOpen === true && "rotate(180deg)"
        }),
        option: (defaultStyles, state) => {
            return {
                ...defaultStyles,
                fontFamily: "Myriad Pro",
                fontSize: " 0.875rem",
                lineHeight: "1rem",
                padding: '0.5rem 1rem ',
                textAlign: "left",
                backgroundColor: state.isSelected ? props.type === 3 ? "bg-transparent" : `${color.blue_violet}` : `${color.white}`,
                color: state.isSelected ? props.type === 3 ? `${color.medium_gray}` : `${color.white}` : `${color.medium_gray}`,
                pointerEvents: props.type !== 3 && state.isSelected && "none",
                "&:hover": {
                    backgroundColor: `${state.isSelected ? color.blue_violet : color.lavender} `,
                    color: `${state.isSelected ? color.white : color.chinese_black} `,
                    cursor: state.isSelected ? "default" : "pointer",
                },
            };
        },

        multiValue: (defaultStyles) => ({
            ...defaultStyles,
            backgroundColor: color.lavender_dark,
            minWidth: "max-content",
            borderRadius: "0.75rem",
            padding: "0.25rem 0.6rem 0.25rem 0.2rem",
            margin: "0 0.5rem 0 0",
            display: "flex",
            gap: "0.25rem",
            alignItems: "center",

        }),
        multiValueLabel: (defaultStyles) => ({
            ...defaultStyles,
            color: color.chinese_black,
            fontFamily: "Myriad Pro",
            fontSize: "0.75rem",
            fontWeight: "400",
            lineHeight: "1rem",
            margin: "0",
            padding: "0",
        }),
        multiValueRemove: (defaultStyles) => ({
            ...defaultStyles,
            color: color.charleston_green_light,
            cursor: "pointer",
            padding: "0",
            "&:hover": {
                color: color.charleston_green_light,
                backgroundColor: "transparent",
            },
        }),
    };

    // style two
    const CUSTOM_STYLES_TWO = {
        container: (defaultStyles, state) => ({
            ...defaultStyles,
            width: "100%",
            '&: focus- visible': {
                outline: 'none'
            }
        }),
        placeholder: (defaultStyles) => ({
            ...defaultStyles,
            margin: "0",
            fontFamily: "Myriad Pro",
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: "1rem",
            color: color.blue_violet
        }),
        menuList: (defaultStyles) => ({
            ...defaultStyles,
            padding: '0',
            maxHeight: '11.25rem',

            '&::-webkit-scrollbar-thumb': {
                background: color.lavender_blue,
                borderRadius: "0.25rem",
            },

            '&::-webkit-scrollbar': {
                width: "0.25rem",
                display: "block",
            }
        }),
        control: (defaultStyles, state) => ({
            ...defaultStyles,
            width: props.controlWidth ? props.controlWidth : "5.6875rem",
            minHeight: "2rem",
            borderRadius: "1.125rem",
            border: '0px',
            backgroundColor: color.soft_lilac,
            boxShadow: `0 0 0.125rem 0 ${color.semi_transparent_black}`,
            overflow: "hidden",
            padding: "0.25rem 0.75rem",
            "&:hover": {
                cursor: 'pointer',
            }
        }),
        singleValue: (defaultStyles, state) => ({
            ...defaultStyles,
            textAlign: "left",
            color: color.blue_violet,
            fontFamily: "Myriad Pro",
            fontSize: "0.75rem",
            lineHeight: "1rem",
        }),
        valueContainer: (defaultStyles) => ({
            ...defaultStyles,
            padding: "0rem",
        }),
        menu: (defaultStyles) => ({
            ...defaultStyles,
            position: 'absolute',
            right: 0,
            left: 'auto',
            boxShadow: ` 8px 16px 56px 0px #0000001F`,
            borderRadius: "0.5rem",
            zIndex: 9999,
            width: props.controlWidth ? props.controlWidth : "5.6875rem",
            padding: '0.5rem 0rem',
            marginBottom: '0.25rem',
            marginTop: '0.25rem',
        }),
        menuPortal: _menuPortalStyle,
        dropdownIndicator: (defaultStyles, state) => ({
            ...defaultStyles,
            padding: "0",
            transition: "transform 0.1s",
            transform: state.selectProps.menuIsOpen === true && "rotate(180deg)"
        }),
        option: (defaultStyles, state) => {
            return {
                ...defaultStyles,
                fontFamily: "Myriad Pro",
                fontSize: " 0.875rem",
                lineHeight: "1rem",
                padding: '0.5rem 1rem ',
                textAlign: "left",
                backgroundColor: state.isSelected ? `${color.blue_violet}` : `${color.white} `,
                color: state.isSelected ? `${color.white} ` : `${color.medium_gray} `,
                pointerEvents: state.isSelected && "none",
                "&:hover": {
                    cursor: state.isSelected ? "default" : "pointer",
                    pointerEvent: state.isSelected ? "none" : "auto",
                    backgroundColor: `${state.isSelected ? color.blue_violet : color.lavender} `,
                    color: `${state.isSelected ? color.white : color.chinese_black} `,
                },
            };
        },
    };


    const _getStyles = () => {
        if (props.type === 1) {
            return CUSTOM_STYLES_TWO;
        }
        else {
            return CUSTOM_STYLES;
        }
    }

    const DropdownIndicator = (dropdown) => {
        return (
            <>
                <components.DropdownIndicator {...dropdown}>
                    {dropdown.selectProps.loader === true ?
                        <div className="spinner-border border-2 e-text-blue-violet w-12px h-12px" role="status"></div> //loader
                        :
                        <Icon icon='dropdown'
                            size={'1rem'}
                            color={props.type === 1 ? null : color.black}
                            className='e-text-dark d-flex z-3'
                        />
                    }

                </components.DropdownIndicator>
            </>
        );
    };

    // const ClearIndicator = (clearProps) => {
    //     return (
    //         <components.ClearIndicator {...clearProps}>
    //             <Icon
    //                 icon='close'
    //                 size={16}
    //                 className='cursor-pointer d-flex'
    //             />
    //         </components.ClearIndicator>
    //     );
    // };


    const ClearIndicator = (clearProps) => {
        // Hide clear button if only "All" option (value: null) is selected
        const hasOnlyNullValue = clearProps.getValue().length === 1 &&
            clearProps.getValue()[0].value === null;

        if (hasOnlyNullValue) {
            return null;
        }

        return (
            <components.ClearIndicator {...clearProps}>
                <div onClick={(e) => {
                    e.stopPropagation();
                    // Find and select the "All" option when clearing
                    const allOption = props.options.find(opt => opt.value === null);
                    if (allOption) {
                        props.onChange([allOption]);
                    }
                }}>
                    <Icon
                        icon='close'
                        size={16}
                        className='cursor-pointer d-flex'
                    />
                </div>
            </components.ClearIndicator>
        );
    };

    const MultiValueRemove = (props) => {
        // Hide remove button if value is null (for "All" option)
        if (props.data.value === null) {
            return null;
        }

        return (
            <components.MultiValueRemove {...props}>
                <Icon
                    icon='close'
                    size={16}
                    className='cursor-pointer d-flex'
                />
            </components.MultiValueRemove>
        );
    };

    return (
        <>
            <div className={`${props.loader ? 'pe-none' : ''} position-relative${props.type === 1 ? ' d-inline-block' : ''}`}>
                {/* <div className='e-font-14 line-height-18px e-text-chinese-black e-font-weight-400 e-primary-font pb-1 '>{props.title}</div> */}

                {props.title &&
                    <label className={`${props.labelStyle} d-flex e-text-chinese-black e-font-14 e-primary-font e-font-weight-400 line-height-18px padding-4px-bottom`}>
                        {props.title}
                        {props.required &&
                            <span className='e-text-carmine-pink'>*</span>
                        }
                    </label>
                }

                <Select value={props.value}
                    // onChange={_handleSelectChange}
                    onChange={(value, action) => _handleSelectChange(value, action)}
                    options={props.options}
                    type={props.type}
                    loader={props.loader}
                    isMulti={props.isMulti}
                    // type 1 (dashboard pills): keep menu in-place so it sits under the control
                    // (menuPortal + html zoom mis-aligns getBoundingClientRect)
                    menuPortalTarget={props.type === 1 ? null : document.body}
                    menuPosition={props.type === 1 ? "absolute" : "fixed"}
                    isSearchable={props.isSearchable ? props.isSearchable : false}
                    menuIsOpen={props.menuIsOpen}
                    placeholder={props.placeholder || "Select..."}
                    styles={_getStyles()}
                    onMenuOpen={props.onMenuOpen}
                    hideSelectedOptions={false}
                    onMenuClose={props.onMenuClose}
                    // components={{
                    //     DropdownIndicator,
                    //     IndicatorSeparator: null,
                    // }}
                    components={{
                        DropdownIndicator,
                        ClearIndicator,
                        MultiValueRemove,
                        IndicatorSeparator: null,
                        ...(props.type === 3 && {
                            Option: CheckboxOption
                        })
                    }}

                />

                {props.error &&
                    <p className={`${style.e_error_message} position-absolute mb-0 e-text-carmine-pink e-primary-font e-font-12 line-height-12px pt-1 e-font-weight-500`}>
                        {props.error}
                    </p>
                }
            </div>

        </>
    );
});

export default CustomSelectBox;
